// Apolo IDE — auth store (Zustand).
//
// Holds the current user + token. Token is mirrored to localStorage so a
// page refresh inside Electron keeps the session.

import { create } from 'zustand';
import { apiFetch, getStoredToken, setStoredToken } from '../lib/api.js';

export const useAuthStore = create((set, get) => ({
  token: getStoredToken(),
  user: null,
  status: 'idle', // idle | loading | error | ready
  error: null,
  bootstrapped: null, // null = unknown, true = registered, false = needs bootstrap
  registeredEmailHint: null,
  // E3 (super-marathon) — multi-profile. Empty by default (account default persona).
  profiles: [],
  activeProfileId: null,

  async refreshBootstrapStatus() {
    set({ status: 'loading', error: null });
    const res = await apiFetch('/api/auth/bootstrap-status');
    if (res.ok) {
      set({
        bootstrapped: !!res.data.bootstrapped,
        registeredEmailHint: res.data.registeredEmailHint || null,
        status: 'ready',
        error: null,
      });
    } else {
      set({ status: 'error', error: res.error || 'failed to reach api', bootstrapped: null, registeredEmailHint: null });
    }
  },

  async login(email, password) {
    set({ status: 'loading', error: null });
    const res = await apiFetch('/api/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    if (!res.ok) {
      let msg = res.error || 'Erro de autenticação';
      const hint = get().registeredEmailHint;
      if (res.code === 'AUTH_EMAIL_NOT_FOUND') {
        msg = hint
          ? `Este email não está cadastrado. Conta nesta API: ${hint}`
          : 'Este email não está cadastrado nesta API.';
      } else if (res.code === 'AUTH_WRONG_PASSWORD') {
        msg = 'Senha incorreta para este email.';
      }
      set({ status: 'error', error: msg });
      return { ok: false, error: msg };
    }
    setStoredToken(res.data.token);
    set({ token: res.data.token, user: res.data.user, status: 'ready', error: null });
    return { ok: true, user: res.data.user };
  },

  async register(email, password, name) {
    set({ status: 'loading', error: null });
    const res = await apiFetch('/api/auth/register', {
      method: 'POST',
      body: { email, password, name },
    });
    if (!res.ok) {
      set({ status: 'error', error: res.error });
      return { ok: false, error: res.error };
    }
    setStoredToken(res.data.token);
    set({
      token: res.data.token,
      user: res.data.user,
      bootstrapped: true,
      status: 'ready',
      error: null,
    });
    return { ok: true, user: res.data.user };
  },

  async hydrateFromToken() {
    const token = get().token;
    if (!token) {
      set({ user: null, status: 'idle' });
      return;
    }
    set({ status: 'loading' });
    const res = await apiFetch('/api/auth/me', { token });
    if (!res.ok) {
      setStoredToken(null);
      set({ token: null, user: null, status: 'idle', error: res.error });
      return;
    }
    set({ user: res.data.user, status: 'ready', error: null });
  },

  logout() {
    setStoredToken(null);
    set({ token: null, user: null, status: 'idle', error: null, profiles: [], activeProfileId: null });
  },

  // E3 — load the account's profiles + which one is active (no trailing slash: the
  // Worker mounts the root route at /api/profiles exactly).
  async fetchProfiles() {
    const res = await apiFetch('/api/profiles');
    if (!res.ok) return { ok: false, error: res.error };
    const nextActive = res.data.active_profile_id || null;
    const changed = get().activeProfileId !== nextActive;
    set({ profiles: res.data.profiles || [], activeProfileId: nextActive });
    // On initial load the active persona resolves HERE (after the first session fetch), so
    // align RECENTS to it — but only when it actually changed, to avoid redundant refetches
    // after a profile edit/import (which also call fetchProfiles but don't switch personas).
    if (changed && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('apolo:profile:changed', { detail: { id: nextActive } }));
    }
    return { ok: true };
  },

  // E3 — "acorda" a persona: activate it server-side, then reflect it locally. Sessions are
  // per-profile, so fire apolo:profile:changed → useAgentStore refetches RECENTS for this
  // persona (everything else — GitHub/MCPs/skills/brain — stays account-level, untouched).
  async switchProfile(id) {
    const res = await apiFetch(`/api/profiles/${id}/activate`, { method: 'POST' });
    if (!res.ok) return { ok: false, error: res.error };
    set({ activeProfileId: res.data.active_profile_id || id });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('apolo:profile:changed', { detail: { id } }));
    }
    return { ok: true };
  },

  // E3 — create a persona (full fields: name/model/instructions/memory/skills). Refetches.
  async createProfile(fields) {
    const body = typeof fields === 'string' ? { name: fields } : (fields || {});
    const res = await apiFetch('/api/profiles', { method: 'POST', body });
    if (!res.ok) return { ok: false, error: res.error };
    await get().fetchProfiles();
    return { ok: true, id: res.data.id };
  },

  // E3 — edit a persona (name/model/instructions/memory/skills). Refetches so the UI updates.
  async updateProfile(id, fields) {
    const res = await apiFetch(`/api/profiles/${id}`, { method: 'PATCH', body: fields || {} });
    if (!res.ok) return { ok: false, error: res.error };
    await get().fetchProfiles();
    return { ok: true };
  },

  // E3 — "Copy setup": the portable JSON for a persona.
  async exportProfile(id) {
    const res = await apiFetch(`/api/profiles/${id}/export`);
    if (!res.ok) return { ok: false, error: res.error };
    return { ok: true, profile: res.data.profile };
  },

  // E3 — paste a "Copy setup" JSON to recreate a persona (onboarding = one paste).
  async importProfile(profileJson) {
    const res = await apiFetch('/api/profiles/import', { method: 'POST', body: { profile: profileJson } });
    if (!res.ok) return { ok: false, error: res.error };
    await get().fetchProfiles();
    return { ok: true, id: res.data.id };
  },
}));

// Sprint v8.3 — auto-logout em token revoked. apiFetch dispara este event
// quando o Worker responde 401 com "Token revoked" (depois de password
// reset, token_version bump, etc). Limpa store → AppShell volta pra
// LoginPage automaticamente.
if (typeof window !== 'undefined') {
  window.addEventListener('apolo:auth:revoked', (e) => {
    useAuthStore.setState({
      token: null,
      user: null,
      status: 'idle',
      error: `Sessão expirada — faça login novamente. (${e?.detail?.reason || 'token revoked'})`,
    });
  });
}
